import { ApolloError } from "@apollo/client";

/**
 * Extracts the first GraphQL error message from Apollo Client error
 *
 * @param {ApolloError|Object} error Apollo Client error object
 * @returns {string|null} First error message or null if no errors found
 */
/**
 * Database/driver noise that must never reach a visitor's screen.
 *
 * The server is the primary defence (updateRegister now returns short human messages), but other
 * resolvers can still surface a driver error, and `error.message` is rendered verbatim in several
 * places. A duplicate-key message like "Duplicate entry 'a@b.com' for key 'Email'" leaks both the
 * value AND the schema, so anything matching these shapes is replaced with a safe sentence.
 */
const SQL_NOISE = /\b(ER_[A-Z_]+|SQLSTATE|Duplicate entry|for key|Unknown column|no such table|syntax error at or near|ECONNREFUSED|knex|mysql|sqlMessage|SELECT |INSERT |UPDATE |DELETE FROM)\b/i;

const SAFE_FALLBACK =
  "Something went wrong while saving. Please try again, or contact the organiser if it keeps happening.";

/** Replace a technical message with a human one; pass through anything already safe. */
export const sanitizeErrorMessage = (message) => {
  if (!message) return null;
  const text = String(message);
  if (SQL_NOISE.test(text)) return SAFE_FALLBACK;
  // A very long message is almost always a stack/driver dump rather than an authored message.
  if (text.length > 300) return SAFE_FALLBACK;
  return text;
};

export const getGraphQLErrorMessage = (error) => {
  if (!error) return null;

  // Check for GraphQL errors array
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return sanitizeErrorMessage(error.graphQLErrors[0].message);
  }

  // Check for network error
  if (error.networkError) {
    return sanitizeErrorMessage(error.networkError.message) || "Network error occurred";
  }

  // Check for direct message property
  if (error.message) {
    return sanitizeErrorMessage(error.message);
  }

  return null;
};

/**
 * Checks if the error is a specific GraphQL error message
 *
 * @param {ApolloError|Object} error Apollo Client error object
 * @param {string} targetMessage The specific error message to check for
 * @returns {boolean} True if error matches the target message
 */
export const isGraphQLError = (error, targetMessage) => {
  const errorMessage = getGraphQLErrorMessage(error);
  return errorMessage === targetMessage;
};

/**
 * Extracts error type based on common GraphQL error patterns
 *
 * @param {ApolloError|Object} error Apollo Client error object
 * @returns {Object} Error information with type and message
 */
export const analyzeGraphQLError = (error) => {
  const message = getGraphQLErrorMessage(error);

  if (!message) {
    return { type: "unknown", message: "An unknown error occurred" };
  }

  // Base flags (owner 2026-07-16): callers (RegistrationIframe / EditRegistration) branch on these
  // booleans; previously they were never set, so those dedicated branches were dead code.
  const base = {
    message,
    isLinkNotFound: message.includes("Link not found"),
    isPermissionDenied: message.includes("deactivated by admin"),
    isRegistrationClosed:
      message.includes("deactivated by admin") ||
      message.includes("registration is closed") ||
      message.includes("no longer available"),
    isRegistrationFull:
      message.includes("Registration limit") ||
      message.includes("exhausted") ||
      message.includes("seats are full") ||
      message.includes("Registration full"),
    isDuplicate:
      message.includes("already registered") ||
      message.includes("already been registered"),
    isRejected: message.includes("declined this invitation"),
  };

  // Check for specific error patterns
  if (base.isPermissionDenied) return { ...base, type: "deactivated" };
  if (base.isLinkNotFound) return { ...base, type: "not_found" };
  if (base.isRegistrationFull) return { ...base, type: "limit_reached" };
  if (message.includes("Network error") || message.includes("connection"))
    return { ...base, type: "network" };

  return { ...base, type: "general" };
};

/**
 * Safely executes a GraphQL query or mutation with proper error handling
 *
 * @param {Function} queryFn Apollo Client query function to execute
 * @param {Object} variables Variables for the query
 * @param {Object} options Additional options
 * @param {Function} options.onSuccess Callback function on success
 * @param {Function} options.onError Custom error handler function
 * @param {boolean} options.throwError Whether to throw the error (defaults to false)
 * @returns {Promise<Object>} Result of the query or mutation
 */
export const safeQueryExecute = async (queryFn, variables, options = {}) => {
  const { onSuccess, onError, throwError = false } = options;

  try {
    const { data, errors } = await queryFn({ variables });

    if (errors && errors.length > 0) {
      const errorMessage = errors.map((err) => err.message).join(", ");
      throw new ApolloError({ graphQLErrors: errors, errorMessage });
    }

    if (onSuccess && typeof onSuccess === "function") {
      onSuccess(data);
    }

    return { data, success: true };
  } catch (error) {
    console.error("API request error:", error);

    const errorMessage =
      getGraphQLErrorMessage(error) || "An unknown error occurred";

    // Handle specific error types
    if (error.networkError) {
      console.error("Network error:", error.networkError);
    }

    if (error.graphQLErrors) {
      console.error("GraphQL errors:", error.graphQLErrors);
    }

    // Call custom error handler if provided
    if (onError && typeof onError === "function") {
      onError(error);
    }

    if (throwError) {
      throw error;
    }

    return {
      success: false,
      error: errorMessage,
      originalError: error,
    };
  }
};

/**
 * Handles payment errors with appropriate user messages
 *
 * @param {Error} error Payment error object
 * @returns {string} User-friendly error message
 */
export const handlePaymentError = (error) => {
  if (!error) return "An unknown payment error occurred";

  // Common payment error types with user-friendly messages
  const paymentErrorMap = {
    card_declined: "Your card was declined. Please try another payment method.",
    insufficient_funds: "Insufficient funds in your account.",
    expired_card: "Your card has expired.",
    invalid_cvc: "The security code (CVC) is invalid.",
    invalid_number: "The card number is invalid.",
    invalid_expiry: "The expiration date is invalid.",
    processing_error: "An error occurred while processing your payment.",
    network_error:
      "Network error occurred. Please check your internet connection.",
  };

  // Check for common error types
  const errorCode = error.code || "";
  if (paymentErrorMap[errorCode]) {
    return paymentErrorMap[errorCode];
  }

  // Extract the most relevant message
  const errorMessage = error.message || "Payment failed";
  return errorMessage;
};

/**
 * Generates a unique transaction ID for payments
 *
 * @returns {string} Unique transaction ID
 */
export const generateTransactionId = () => {
  return `trx_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
};

/**
 * Debounces a function call to prevent multiple rapid executions
 *
 * @param {Function} func Function to debounce
 * @param {number} wait Milliseconds to wait
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Formats a monetary amount with currency symbol
 *
 * @param {number} amount Amount to format
 * @param {string} currency Currency code (USD, EUR, etc.)
 * @returns {string} Formatted amount with currency symbol
 */
export const formatCurrency = (amount, currency = "USD") => {
  if (amount === undefined || amount === null) return "";

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    console.error("Currency formatting error:", error);
    return `${currency} ${amount.toFixed(2)}`;
  }
};
